import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { addAlert } from '@/features/Notifications/notificationSlice'
import RefundItems from './RefundForm/RefundItems'
import TypeOptions from './RefundForm/TypeOptions'
import TipAmount from './RefundForm/TipAmount'
import CustomPayment from './RefundForm/CustomPayment'
import { useConfirm } from '@/lib/useConfirmHook'
import { numberToCurrency } from '@/lib/Number'
import { accessToken } from '@/lib/Csrf'
import titleize from '@/lib/String'
import { debug } from '@/lib/Debug'

export default function RefundForm({
    companyId,
    locationId,
    checkNumber,
    payment,
    tabs,
    isTipRefunded,
    isNonSplitBookingRefunded,
    previousRefundTypes=[],
    refundedUuids
}) {
    const dispatch                                = useDispatch()
    const { confirm }                             = useConfirm()
    const [notes, setNotes]                       = useState('')
    const [email, setEmail]                       = useState(payment.email || '')
    const [methodOptions, setMethodOptions]       = useState([])
    const [selectedMethod, setSelectedMethod]     = useState('credit')
    const [selectedTabItems, setSelectedTabItems] = useState([])
    const [includesTip, setIncludesTip]           = useState(false)
    const [customAmount, setCustomAmount]         = useState(0)

    // set the chosen refund option to that of the first available previousRefundType as now
    // the user should be "stuck" in a certain refund flow (there should only be one available)
    const [refundOption, setRefundOption] = useState(previousRefundTypes?.length === 1 ? previousRefundTypes[0] : 'full')

    const maxRefundableAmount = (payment.amount_refundable_cents - (isTipRefunded ? 0 : payment.tip_cents)) / 100
    const maxRefundableCents  = Number((maxRefundableAmount * 100).toFixed(0))

    const confirmationEmail = () => confirm('Refund without sending emailed receipt?')

    const confirmationGeneral = () => confirm(
        `<p>Are you sure?</p>${selectedMethod == 'gift_card' ? '' : '<p>This cannot be undone.</p>'}`,
        { dangerous: selectedMethod != 'gift_card' }
    )

    const confirmationCustom = () => confirm(
        `<p>Is the refund amount correct?</p>${selectedMethod == 'gift_card' ? '' : '<p>This cannot be undone.</p>'}`,
        { dangerous: selectedMethod != 'gift_card' }
    )

    const hasRefundableTip = isTipRefunded ? false : payment.tip_cents > 0
    const noTabsPresent    = (tabs || []).length === 0

    const amountCents = useMemo(() => {
        let total = 0

        if (refundOption === 'full') {
            total += payment.amount_refundable_cents - (isTipRefunded ? 0 : payment.tip_cents)
        } else if (/custom/i.test(refundOption)) {
            total += Number(customAmount) * 100
        } else {
            total += selectedTabItems.reduce((totalSelected, tab) => {
                totalSelected += (tab.items || []).reduce((sum, item) => sum + item.total_received_cents, 0)
                totalSelected += (tab.booking_items || []).reduce((sum, item) => sum + item.total_received_cents, 0)
                totalSelected += tab.total_received_booking_cents || 0
                return totalSelected
            }, 0)
        }

        if (includesTip) {
            total += payment.tip_cents
        }

        // NOTE
        // the parseFloat/toFixed combo is in here specifically
        // to combat floating point math issues.
        //
        // ex: (0 + 39.55) * 100 = 3954.9999999999995 instead of 3954 as expected
        return Number.parseFloat(total.toFixed(2))
    }, [refundOption, selectedTabItems, includesTip, customAmount])

    const handleProcessRefund = async (e) => {
        e.preventDefault()

        if (debug && console) { console.error(`amount: ${amountCents} | refundable amount: ${payment.amount_refundable_cents}`) }

        if (amountCents > payment.amount_refundable_cents) {
            dispatch(addAlert({ type: 'error', text: `Refund amount cannot be more than ${numberToCurrency(payment.amount_refundable_cents / 100)}` }))
            if (debug && console) { console.error(`amount: ${amountCents} | refundable amount: ${payment.amount_refundable_cents}`) }
            return
        }

        switch(refundOption) {
            case 'full' : {
                const deepCopy = JSON.parse(JSON.stringify(payment.adv_pos_tab_transaction_splits))

                const advPosTransactionSplits = deepCopy.map(split => {
                    delete split['total_received_cents']

                    return {
                        ...split,
                        items: (split.items || []).filter(item => !refundedUuids.includes(item.uuid)),
                        booking_items: (split.booking_items || []).filter(item => !refundedUuids.map(i => i.uuid).includes(item.uuid)),
                        total_received_booking_cents: (!isNonSplitBookingRefunded && (!split.booking_items || split.booking_items.length === 0)) ? split.total_received_booking_cents : 0,
                        booking_taxes_and_fees: (!isNonSplitBookingRefunded && (!split.booking_items || split.booking_items.length === 0)) ? split.booking_taxes_and_fees : {}
                    }
                })

                if (console && debug) { console.log(advPosTransactionSplits) }

                if (await (email !== '' || confirmationEmail())) {
                    if (await confirmationGeneral()) {
                        processRefund(advPosTransactionSplits)
                    }
                }
                break
            }

            case 'partial_by_item' : {
                const advPosTransactionSplits = selectedTabItems

                if (console && debug) { console.log(advPosTransactionSplits) }

                if (await (email !== '' || confirmationEmail())) {
                    if (await confirmationGeneral()) {
                        processRefund(advPosTransactionSplits)
                    }
                }
                break
            }

            case 'custom_pre_payment' : {
                const generalTab  = tabs.find(tab => tab.is_general)
                const creditItem  = generalTab.items.find(item => /^booking_(pre_payment|deposit)/i.test(item.item.type) && item.item.id === payment.id)
                const amountCents = (customAmount * 100).toFixed(0)

                const advPosTransactionSplits = [{
                    tab_id: generalTab.id,
                    booking_items: [{
                        uuid: creditItem.item.uuid,
                        total_received_cents: amountCents,
                        name: creditItem.item.name,
                    }],
                    total_received_booking_cents: amountCents,
                }]

                if (console && debug) { console.log(advPosTransactionSplits) }

                if (await (email !== '' || confirmationEmail())) {
                    if (await confirmationCustom()) {
                        processRefund(advPosTransactionSplits)
                    }
                }
                break
            }

            case 'custom_payment' : {
                let allocatedCents      = 0
                const targetAmount      = Number(customAmount)
                const targetCents       = targetAmount * 100
                const totalCents        = payment.amount_refundable_cents - (!isTipRefunded ? payment.tip_cents : 0)
                const perItemPercentage = (((targetAmount * 100) / totalCents) / 100) * 100
                const deepCopy          = JSON.parse(JSON.stringify(payment.adv_pos_tab_transaction_splits))

                const findTheLargestItem = () => {
                    let largestItem = null

                    advPosTransactionSplits.forEach(split => {
                        const tabItems       = (split.items || []).concat(split.booking_items || [])
                        const largestTabItem = tabItems.reduce((p,c) => (p.total_received_cents > c.total_received_cents ? p : c), { total_received_cents: 0 })

                        if (largestItem === null || largestTabItem?.total_received_cents > largestItem?.total_received_cents) {
                            largestItem = JSON.parse(JSON.stringify(largestTabItem)) // deep copy
                        }

                        if (!isNonSplitBookingRefunded && (split?.total_received_booking_cents || 0) > largestTabItem?.total_received_cents) {
                            largestItem = 'NON_SPLIT_BOOKING_TOTAL_RECEIVED'
                        }
                    })

                    return largestItem
                }

                const adjustedCentsForRefund = (amount=0) => {
                    if (amount === 0) { return amount }

                    const refundedAmount = ((amount * perItemPercentage) / 100).toFixed(2)
                    const refundedCents  = Number((refundedAmount * 100).toFixed(0))

                    if (debug && console) { console.log({ refundedAmount, refundedCents }) }
                    allocatedCents += refundedCents

                    return refundedCents
                }

                const findAndAdjustItemInSplitByAmount = (amount=0, action=null) => {
                    const target = findTheLargestItem()

                    if (target === 'NON_SPLIT_BOOKING_TOTAL_RECEIVED') {
                        advPosTransactionSplits.forEach(split => {
                            if (!!amount && split?.total_received_booking_cents > 0) {
                                if (action === 'add') {
                                    if (console && debug) { console.log('Adding', amount, 'to unsplit booking') }
                                    split.total_received_booking_cents = Number((split.total_received_booking_cents + amount).toFixed(0))
                                    allocatedCents += amount
                                }
                                if (action === 'subtract') {
                                    if (console && debug) { console.log('Subtracting', amount, 'from unsplit booking') }
                                    split.total_received_booking_cents = Number((split.total_received_booking_cents - amount).toFixed(0))
                                    allocatedCents -= amount
                                }
                            }
                        })
                    } else {
                        advPosTransactionSplits.forEach(split => {
                            const tabItems = (split.items || []).concat(split.booking_items || [])
                            const item     = tabItems.find(i => i.uuid === target.uuid)

                            if (!!item && !!amount) {
                                if (action === 'add') {
                                    if (console && debug) { console.log('Adding', amount, `to ${item.name} (${item.uuid})`) }
                                    item.total_received_cents = Number((item.total_received_cents + amount).toFixed(0))
                                    allocatedCents += amount
                                }
                                if (action === 'subtract') {
                                    if (console && debug) { console.log('Subtracting', amount, `from ${item.name} (${item.uuid})`) }
                                    item.total_received_cents = Number((item.total_received_cents - amount).toFixed(0))
                                    allocatedCents -= amount
                                }
                            }
                        })
                    }
                }

                // generate the refund's transaction splits and allocate the refund
                // amount per item based the previously calculated percentage
                let advPosTransactionSplits = deepCopy.map(split => {
                    delete split['total_received_cents']
                    delete split['booking_taxes_and_fees']
                    delete split['food_and_beverage_taxes_and_fees']
                    delete split['total_received_fab_cents']

                    let newSplit = {
                        ...split,
                        total_received_booking_cents: (
                            (!isNonSplitBookingRefunded && (!split.booking_items || split.booking_items.length === 0))
                                ? adjustedCentsForRefund(split.total_received_booking_cents)
                                : 0
                        ),
                        booking_items: (
                            (split.booking_items || [])
                                .filter(item => !refundedUuids.map(i => i.uuid).includes(item.uuid))
                                .map(item => ({ ...item, total_received_cents: adjustedCentsForRefund(item.total_received_cents) }))
                                .filter(item => item.total_received_cents > 0)
                        ),
                        items: (
                            (split.items || [])
                                .filter(item => !refundedUuids.includes(item.uuid))
                                .map(item => ({ ...item, total_received_cents: adjustedCentsForRefund(item.total_received_cents) }))
                                .filter(item => item.total_received_cents > 0)
                        ),
                    }

                    if (newSplit?.total_received_booking_cents === 0) { delete newSplit['total_received_booking_cents'] }
                    if (newSplit?.booking_items?.length === 0)        { delete newSplit['booking_items']                }
                    if (newSplit?.items?.length === 0)                { delete newSplit['items']                        }

                    return newSplit
                })
                .filter(split => (
                    Object.keys(split).includes('items')                        ||
                    Object.keys(split).includes('booking_items')                ||
                    Object.keys(split).includes('total_received_booking_cents')
                ))

                // calculate the diffCents after the split items have been adjusted
                const diffCents = (targetCents - allocatedCents)

                // detect and handle any rounding errors by
                // allocating the remaining cents to the largest item
                if (targetCents > allocatedCents) {
                    if (debug && console) {
                        console.log(
                            'Division Remainder! Allocating the remaining cents...',
                            { targetCents, allocatedCents, diffCents }
                        )
                    }
                    findAndAdjustItemInSplitByAmount(diffCents, 'add')
                }

                // detect and handle any overallocation by
                // reducing the largest item by the difference
                if (diffCents < 0) {
                    if (debug && console) {
                        console.log(
                            'Over Allocated! Reducing the additional cents...',
                            { targetCents, allocatedCents, diffCents }
                        )
                    }
                    findAndAdjustItemInSplitByAmount(diffCents * -1, 'subtract')
                }

                if (debug && console) {
                    console.log({
                        targetAmount,
                        targetCents,
                        totalCents,
                        allocatedCents,

                        // instead of referencing diffCents again,
                        // re-calculate to ensure its accuracy
                        diffCents: (targetCents - allocatedCents),

                        perItemPercentage,
                        refundedUuids,
                        advPosTransactionSplits,
                    })
                }

                if (allocatedCents !== targetCents) {
                    dispatch(addAlert({ type: 'error', text: 'Unable to correctly allocate refund amount!' }))
                    throw(`Refund amount is not correct! ${allocatedCents} != ${targetCents}`)
                }

                if (await (email !== '' || confirmationEmail())) {
                    if (await confirmationCustom()) {
                        processRefund(advPosTransactionSplits)
                    }
                }
                break
            }
        }
    }

    const processRefund = (advPosTransactionSplits) => {
        axios.post(
            `/companies/${companyId}/locations/${locationId}/pos/checks/${checkNumber}/payments/${payment.id}/refunds`,
            {
                authenticity_token: accessToken,
                refund_option: refundOption,
                amount_cents: amountCents,
                includes_tip: includesTip,
                email: email || payment.email,
                notes: notes,
                refund_method: selectedMethod,
                adv_pos_refund_type: refundOption,
                adv_pos_transaction_splits: advPosTransactionSplits
            }
        )
        .then(({ data }) => {
            if (data.success) {
                window.location.href = `/companies/${companyId}/locations/${locationId}/pos/checks/${checkNumber}`
                dispatch(addAlert({ type: 'success', text: 'Refund Processed' }))
            } else {
                dispatch(addAlert({ type: 'error', text: data.message }))
            }
        })
    }

    useEffect(() => {
        setMethodOptions(
            payment.transaction_type === 'credit'
                ? ['credit_card', 'gift_card']
                : Object.keys(payment.available_refund_methods)
        )

        setSelectedMethod(
            payment.transaction_type === 'credit'
                ? 'credit_card'
                : Object.keys(payment.available_refund_methods)[0]
        )

        if (!payment.check_id) {
            setRefundOption('custom_pre_payment')
            setCustomAmount(((payment.amount_refundable_cents - (isTipRefunded ? 0 : payment.tip_cents)) / 100.0).toFixed(2))
        }
    }, [])

    return (
        <form onSubmit={handleProcessRefund}>
            {
                refundOption === 'custom_pre_payment' ? (
                    <CustomPayment
                        payment={payment}
                        customAmount={customAmount}
                        maxRefundableAmount={maxRefundableAmount}
                        isTipRefunded={isTipRefunded}
                        includesTip={includesTip}
                        className='mt-5 pt-3'
                        setCustomAmount={setCustomAmount}
                        setIncludesTip={setIncludesTip}
                        isVisible
                    />
                ) : <>
                    <TypeOptions
                        fullOrRemaining={payment.payment_amount_cents === maxRefundableCents ? 'full' : 'remaining'}
                        previousRefundTypes={previousRefundTypes}
                        refundOption={refundOption}
                        setRefundOption={setRefundOption}
                    />

                    {
                        ((refundOption === 'full' && hasRefundableTip) || refundOption !== 'full') && (
                            <hr className='mt-0 mb-3 border-thick' />
                        )
                    }

                    <RefundItems
                        isVisible={refundOption === 'partial_by_item'}
                        tabs={tabs}
                        payment={payment}
                        refundOption={refundOption}
                        isNonSplitBookingRefunded={isNonSplitBookingRefunded}
                        refundedUuids={refundedUuids}
                        selectedTabItems={selectedTabItems}
                        setSelectedTabItems={setSelectedTabItems}
                    />

                    <CustomPayment
                        isVisible={refundOption === 'custom_payment'}
                        payment={payment}
                        customAmount={customAmount}
                        maxRefundableAmount={maxRefundableAmount}
                        isTipRefunded={isTipRefunded}
                        includesTip={includesTip}
                        className='mt-3'
                        setCustomAmount={setCustomAmount}
                        setIncludesTip={setIncludesTip}
                    />

                    <TipAmount
                        isVisible={hasRefundableTip && !isTipRefunded && /^(full|partial_by_item)$/i.test(refundOption) }
                        className='px-3 mb-3'
                        payment={payment}
                        includesTip={includesTip}
                        setIncludesTip={setIncludesTip}
                    />
                </>
            }

            <hr className='mt-0 mb-3 border-thick' />

            <div className='row mt-4'>
                <div className='col-6'>
                    <div className='form-group'>
                        <label htmlFor='refund_method'>
                            Refund Method
                        </label>
                        {
                            methodOptions.map((option, index) => (
                                <div key={index}>
                                    <input
                                        type='radio'
                                        name={`refund_method_${option}`}
                                        id={`refund_method_${option}`}
                                        value={option}
                                        checked={selectedMethod === option}
                                        className='radio-tab-input mr-1 ml-3'
                                        onChange={e => setSelectedMethod(e.target.value)}
                                    />
                                    <label htmlFor={`refund_method_${option}`} className='pl-2'>{titleize(option)}</label>
                                </div>
                            ))
                        }
                    </div>
                </div>

                <div className='col-6'>
                    <div className='form-group'>
                        <label className='email' htmlFor='email'>Receipt Email</label>
                        <input
                            type='email'
                            name='email'
                            id='email'
                            className='form-control'
                            value={email}
                            placeholder={payment.email || ''}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>

                    <div className='form-group'>
                        <label htmlFor='notes'>Notes</label>
                        <textarea
                            name='notes'
                            className='form-control'
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <hr className='mt-2 mb-0 border-thick' />

            <div className='pt-3 text-right d-flex align-items-center justify-content-between'>
                <strong>
                    <span children='Refund Amount: ' />
                    {
                        amountCents !== '' && amountCents > 0 ? (
                            <span
                                className={[
                                    'pl-2',
                                    amountCents > 0 && (amountCents - (includesTip ? payment.tip_cents : 0)) <= maxRefundableCents
                                        ? 'text-success'
                                        : 'text-danger fa-fade'
                                ].join(' ')}
                                children={ numberToCurrency((amountCents / 100).toFixed(2)) }
                            />
                        ) : (
                            <span className='pl-2' children='—' />
                        )
                    }
                </strong>

                <button
                    type='submit'
                    className='btn btn-primary ml-auto'
                    disabled={
                        amountCents === ''
                        || amountCents <= 0
                        || (amountCents - (includesTip ? payment.tip_cents : 0) > maxRefundableCents)
                        || noTabsPresent
                    }
                    children='Process Refund'
                />
            </div>

            {
                noTabsPresent && (
                    <div className='alert alert-danger rounded py-2 w-100 mt-3 mb-0 font-weight-bold text-center'>
                        Cannot continue to process refund! Not enough data present!
                    </div>
                )
            }
        </form>
    )
}
